import React, { useEffect, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { RichTextEditorToolbar } from './RichTextEditorToolbar';
import {
  normalizeRichTextHtml,
  valueToEditorContent,
} from './richTextUtils';
import './RichTextEditor.scss';

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  label,
  placeholder = '',
  readOnly = false,
  className = '',
}) => {
  const lastEmittedRef = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: valueToEditorContent(value) || '<p></p>',
    editable: !readOnly,
    immediatelyRender: false,
    onUpdate: ({ editor: currentEditor }) => {
      const html = normalizeRichTextHtml(currentEditor.getHTML());
      lastEmittedRef.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor) {
      lastEmittedRef.current = normalizeRichTextHtml(value);
      return;
    }

    const normalizedValue = normalizeRichTextHtml(value);
    if (normalizedValue === lastEmittedRef.current) return;

    lastEmittedRef.current = normalizedValue;
    const nextContent = valueToEditorContent(value) || '<p></p>';
    const currentContent = normalizeRichTextHtml(editor.getHTML());

    if (nextContent !== currentContent) {
      editor.commands.setContent(nextContent, { emitUpdate: false });
    }
  }, [value, editor]);

  const rootClassName = [
    'rich-text-editor',
    readOnly && 'rich-text-editor--readonly',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={rootClassName}>
      {(label || !readOnly) && (
        <div className="rich-text-editor__head">
          {label && <label className="rich-text-editor__label">{label}</label>}
          {!readOnly && <RichTextEditorToolbar editor={editor} />}
        </div>
      )}
      <EditorContent editor={editor} className="rich-text-editor__content" />
    </section>
  );
};
